import findIndexByInstancekey from './findIndexByInstancekey';
import findIndexToInsertAt from './findIndexToInsertAt';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import getFocusedFilterForTable from 'mini-mail-list-store/lib/utils/getFocusedFilterForTable';
import isSortKeyEqual from 'mini-mail-list-response-processor/lib/actions/helpers/isSortKeyEqual';
import removeRowsFromListViewStoreInternal from '../../triage/removeRowsFromListViewStoreInternal';
import TableOperations from 'mini-mail-list-table-operations';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import { fetchRichContentForRows } from 'mini-mail-list-response-processor/lib/actions/helpers/fetchRichContentForRows';
import { getConversationOrItemIdFromRow } from 'mini-mail-list-table-operations/lib/utils/getConversationOrItemIdFromRow';
import { getDateSortTimeStampFromRowData } from './getDateSortTimeStampFromRowData';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { lazyPrefetchItem } from 'mini-mail-prefetch';
import { lazyUpdateRollupForView } from 'mini-mail-focused-inbox-rollup-store';
import { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';
import { shouldProcessServerPayload } from 'mini-mail-list-response-processor';

/**
 * Performs update for a row that already exists on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt - the index to insert the row at
 */
function updateExistingRow(
    row: MailListRowDataType,
    tableView: TableView,
    indexToInsertAt: number
) {
    const isSortKeySame = isSortKeyEqual(row, tableView);

    if (isSortKeySame) {
        // Only update data if sort key is the same
        TableOperations.updateRowData(row, tableView);
    } else {
        // Update data and row position otherwise
        TableOperations.updateRow(indexToInsertAt, row, tableView);
    }
}

/**
 * Inserts a new row that does not exist on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt - the index to insert the row at
 */
function addNewRow(row: MailListRowDataType, tableView: TableView, indexToInsertAt: number) {
    TableOperations.addRow(indexToInsertAt, row, tableView);

    // Also increment current loaded index so the new row will be guaranteed to show up in the view
    // without bumping out an existing item
    tableView.currentLoadedIndex++;
}

/**
 * This function handles rowAdd and rowModified notifications.
 * @param row present in the notification
 * @param tableview for which notification has arrived
 * which we will insert the new row payload
 */
export default action('rowNotificationHandler.onAddOrUpdateRow')(async function onAddOrUpdateRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    if (!row.InstanceKey) {
        throw new Error('onAddOrUpdateNotification: Conversation instanceKey should not be null');
    }

    // Pre-process server row and determine whether to consume it further
    if (
        isMiniFeatureEnabled('mini-markAsJunkMultiSelect') &&
        !shouldProcessServerPayload(row, tableView)
    ) {
        return;
    }

    const { lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp } = getDateSortTimeStampFromRowData(
        row,
        tableView.tableQuery.listViewType
    );

    let indexToInsertAt = findIndexToInsertAt(
        lastDeliveryOrRenewTimeStamp,
        lastDeliveryTimeStamp,
        tableView,
        0 /* startIndex */
    );
    const indexOfElement = findIndexByInstancekey(row, tableView);
    const isRowAlreadyPresent = indexOfElement >= 0;

    if (isRowAlreadyPresent && indexOfElement < indexToInsertAt) {
        // If we're moving an existing item down in the list, decrease index to insert by 1 to account for the row being
        // removed from above the indexToInsertAt
        indexToInsertAt--;
    }

    if (indexToInsertAt === -1) {
        if (isRowAlreadyPresent) {
            // Remove the row if could not place it, because the row could have moved down (and out of loaded range) for any reason from another client,
            // e.g unpin or delete the latest item in the conversation from another client
            removeRowsFromListViewStoreInternal([getConversationOrItemIdFromRow(row)], tableView);
        }

        // Ignore the notification if we could not find an index to insert it
        return;
    }

    if (isRowAlreadyPresent) {
        updateExistingRow(row, tableView, indexToInsertAt);
    } else {
        addNewRow(row, tableView, indexToInsertAt);
    }

    // Update focused inbox rollup data for Focused/Other notifications
    if (getFocusedFilterForTable(tableView) !== FocusedViewFilter.None) {
        (await lazyUpdateRollupForView.import())(tableView);
    }

    lazyPrefetchItem.importAndExecute(
        getConversationOrItemIdFromRow(row),
        tableView,
        true /* updateIfExists */
    );

    fetchRichContentForRows(tableView.id);
});
